import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressHeader from '../../components/ui/ProgressHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OverallProgressCard from './components/OverallProgressCard';
import ChapterProgressCard from './components/ChapterProgressCard';
import LearningTimeline from './components/LearningTimeline';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import AchievementsBadges from './components/AchievementsBadges';
import ExportProgress from './components/ExportProgress';
import ChapterProgressAccordion from './components/ChapterProgressAccordion';
import { useAuth } from 'context/AuthContext';
import { CourseAPI } from 'service';


const LearningProgressTracker = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const navigate = useNavigate();

  const chaptersData2 = [
    {
      id: '1',
      title: "Chapter 1: Introduction to AI",
      description: "Fundamentals of artificial intelligence and machine learning",
      status: "not-started",
      progress: 0,
      timeSpent: "0h 0m",
      averageScore: 0,
      attempts: 0,
      mastery: 0,
      presentations: [
        { id: 10, title: "AI Ethics", completed: false, duration: "20 min" },
        { id: 11, title: "Bias in AI", completed: false, duration: "25 min" },
        { id: 12, title: "Future of AI", completed: false, duration: "22 min" }
      ],
      quizzes: [
        { id: 7, title: "Ethics Quiz", completed: false, score: null },
        { id: 8, title: "Future Trends", completed: false, score: null }
      ],
      achievements: [
        { id: 7, title: "Ethics Guardian", earned: false },
        { id: 8, title: "Future Thinker", earned: false }
      ]
    },
    {
      id: '2',
      title: "Chapter 2: Machine Learning Basics",
      description: "Introduction to machine learning concepts and algorithms",
      status: "not-started",
      progress: 0,
      timeSpent: "0h 0m",
      averageScore: 0,
      attempts: 0,
      mastery: 0,
      presentations: [
        { id: 10, title: "AI Ethics", completed: false, duration: "20 min" },
        { id: 11, title: "Bias in AI", completed: false, duration: "25 min" },
        { id: 12, title: "Future of AI", completed: false, duration: "22 min" }
      ],
      quizzes: [
        { id: 7, title: "Ethics Quiz", completed: false, score: null },
        { id: 8, title: "Future Trends", completed: false, score: null }
      ],
      achievements: [
        { id: 7, title: "Ethics Guardian", earned: false },
        { id: 8, title: "Future Thinker", earned: false }
      ]
    },
    {
      id: '3',
      title: "Chapter 3: Advanced Prompting Techniques",
      description: "Master advanced AI prompting strategies and best practices",
      status: "not-started",
      progress: 0,
      timeSpent: "0h 0m",
      averageScore: 0,
      attempts: 0,
      mastery: 0,
      presentations: [
        { id: 10, title: "AI Ethics", completed: false, duration: "20 min" },
        { id: 11, title: "Bias in AI", completed: false, duration: "25 min" },
        { id: 12, title: "Future of AI", completed: false, duration: "22 min" }
      ],
      quizzes: [
        { id: 7, title: "Ethics Quiz", completed: false, score: null },
        { id: 8, title: "Future Trends", completed: false, score: null }
      ],
      achievements: [
        { id: 7, title: "Ethics Guardian", earned: false },
        { id: 8, title: "Future Thinker", earned: false }
      ]
    },
    {
      id: '4',
      title: "Chapter 4: AI Ethics and Future",
      description: "Explore ethical considerations and future of AI technology",
      status: "not-started",
      progress: 0,
      timeSpent: "0h 0m",
      averageScore: 0,
      attempts: 0,
      mastery: 0,
      presentations: [
        { id: 10, title: "AI Ethics", completed: false, duration: "20 min" },
        { id: 11, title: "Bias in AI", completed: false, duration: "25 min" },
        { id: 12, title: "Future of AI", completed: false, duration: "22 min" }
      ],
      quizzes: [
        { id: 7, title: "Ethics Quiz", completed: false, score: null },
        { id: 8, title: "Future Trends", completed: false, score: null }
      ],
      achievements: [
        { id: 7, title: "Ethics Guardian", earned: false },
        { id: 8, title: "Future Thinker", earned: false }
      ]
    }
  ];

  const { courseOverview } = useAuth()

  const [dashboardData, setDashboardData] = useState([]);

  useEffect(() => {
    if (!courseOverview?.coursesProgress) return;

    const progressMap = Object.fromEntries(
      courseOverview.coursesProgress.map(progress => [progress.courseRef, progress])
    );

    const mergedData = chaptersData2.map(course => {

      const progress = progressMap[course.id];

      if (progress?.progress > 0 && progress?.progress < 100) {
        course.status = 'in-progress';
      } else if (progress?.progress === 100) {
        course.status = 'completed';
      } else {
        course.status = 'not-started';
      }

      return {
        ...course,
        ...(progress && {
          progress: progress.progress ?? course.progress,
          averageScore: progress.bestScore ?? course.averageScore,
          timeSpent: progress.timeSpentInMinutes ?? course.timeSpentInMinutes,
          numberOfSlides: progress.numberOfSlides ?? course.numberOfSlides,
          currentSlide: progress.currentSlide ?? course.currentSlide,
          quizzesCount: progress.quizzesCount ?? course.quizzesCount,
          bestScore: progress.bestScore ?? course.bestScore,
        })
      };
    });

    setDashboardData(mergedData);
  }, [courseOverview]);

  const [timeLine, setTimeLine] = useState([]);

  useEffect(() => {
    const fetchTimeline = async () => {
      const response = await CourseAPI.getTimeline();
      if (response.success) {
        setTimeLine(response.data.data.timeline);
      } else {
        console.error("Failed to fetch timeline:", response.error);
      }
    };

    fetchTimeline();
  }, [courseOverview]);

  // Mock data for chapters
  const chaptersData = [
    {
      id: 1,
      title: "Introduction to AI",
      description: "Fundamental concepts and overview of artificial intelligence, covering history, applications, and basic terminology",
      presentationProgress: 100,
      quizAttempts: 2,
      bestScore: 85,
      timeSpent: 45,
      status: "completed",
      isUnlocked: true,
      totalSlides: 12,
      completedSlides: 12,
      lastAccessed: "2024-01-10"
    },
    {
      id: 2,
      title: "Machine Learning Basics",
      description: "Core concepts of machine learning including supervised, unsupervised learning, and common algorithms",
      presentationProgress: 100,
      quizAttempts: 1,
      bestScore: 78,
      timeSpent: 52,
      status: "completed",
      isUnlocked: true,
      totalSlides: 15,
      completedSlides: 15,
      lastAccessed: "2024-01-12"
    },
    {
      id: 3,
      title: "Neural Networks & Deep Learning",
      description: "Advanced topics covering neural network architectures, deep learning principles, and practical applications",
      presentationProgress: 60,
      quizAttempts: 0,
      bestScore: 0,
      timeSpent: 25,
      status: "in-progress",
      isUnlocked: true,
      totalSlides: 18,
      completedSlides: 11,
      lastAccessed: "2024-01-15"
    }
  ];

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'LayoutDashboard',
      description: 'General progress summary'
    },
    {
      id: 'chapters',
      label: 'Chapters',
      icon: 'BookOpen',
      description: 'Chapter-wise progress'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'BarChart3',
      description: 'Performance insights'
    },
    {
      id: 'achievements',
      label: 'Achievements',
      icon: 'Award',
      description: 'Badges and certificates'
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: 'Calendar',
      description: 'Learning journey'
    }
  ];


  const getRecommendedActions = () => {

    const getCurrentCourse = localStorage.getItem('currentChapter');
    const chaptersData = getCurrentCourse ? JSON.parse(getCurrentCourse) : [];
    const actions = [];

    // Check for incomplete chapters
    const inProgressChapter = chaptersData
    if (inProgressChapter) {
      actions.push({
        title: 'Continue Learning',
        description: `Resume Chapter ${inProgressChapter.id}: ${inProgressChapter.title}`,
        icon: 'Play',
        color: 'primary',
        link: `/interactive-presentation-viewer?courseId=${inProgressChapter.id}`
      });
    }

    // Check for retake opportunities
    const lowScoreChapter = courseOverview?.coursesProgress?.find(c => c.bestScore > 0 && c.bestScore < 80);
    if (lowScoreChapter) {
      actions.push({
        title: 'Improve Score',
        description: `Retake Chapter ${lowScoreChapter.id} quiz to improve your ${lowScoreChapter.bestScore}% score`,
        icon: 'RotateCcw',
        color: 'warning',
        link: `/quiz-assessment-interface?chapterId=${lowScoreChapter.id}`
      });
    }



    // Check for next chapter
    const nextChapter = courseOverview?.coursesProgress?.find(c => c.status === 'locked' && c.isUnlocked);
    if (nextChapter) {
      actions.push({
        title: 'Start Next Chapter',
        description: `Begin Chapter ${nextChapter.id}: ${nextChapter.title}`,
        icon: 'ArrowRight',
        color: 'success',
        link: '/interactive-presentation-viewer'
      });
    }

    return actions;
  };

  const recommendedActions = getRecommendedActions();

  const handleNavigateToChapter = (chapterId, section) => {
    switch (section) {
      case 'presentation': navigate(`/interactive-presentation-viewer?courseId=${chapterId}`);
        break;
      case 'quiz': navigate(`/quiz-assessment-interface?chapterId=${chapterId}`);
        break;
      case 'results': navigate('/quiz-results-dashboard');
        break;
      default:
        navigate('/chapter-overview-dashboard');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <OverallProgressCard />

            {/* Quick Actions */}
            {recommendedActions.length > 0 && (
              <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
                <h3 className="mb-4 text-lg font-semibold font-heading text-text-primary">
                  Recommended Actions
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {recommendedActions.map((action, index) => (
                    <Link key={index} to={action.link}>
                      <div className={`
                        bg-${action.color}-50 border border-${action.color}-200 rounded-lg p-4 
                        hover:border-${action.color}-300 transition-colors duration-200 cursor-pointer
                      `}>
                        <div className="flex items-start space-x-3">
                          <div className={`
                            flex items-center justify-center w-10 h-10 bg-${action.color}-100 rounded-lg
                          `}>
                            <Icon
                              name={action.icon}
                              size={18}
                              color={`var(--color-${action.color})`}
                              strokeWidth={2}
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className={`font-heading font-semibold text-${action.color}-800 mb-1`}>
                              {action.title}
                            </h4>
                            <p className={`text-sm text-${action.color}-700`}>
                              {action.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <ChapterProgressAccordion
              chapters={dashboardData}
              onNavigateToChapter={handleNavigateToChapter}
            />

            {/* Recent Activity Summary */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
                <h3 className="mb-4 text-lg font-semibold font-heading text-text-primary">
                  Recent Activity
                </h3>
                {timeLine.length === 0 ? (
                  <p className="text-text-secondary">No recent activity found.</p>
                ) : (
                  <div className="space-y-3">
                    {timeLine.slice(0, 2).map((activity, index) => (
                      <div key={index} className={`flex items-center p-3 space-x-3 rounded-lg bg-${activity.type === 'completed' ? 'success' : 'secondary'}-50`}>
                        <Icon name={activity.type === 'completed' ? 'CheckCircle2' : 'Play'} size={16} color={`var(--color-${activity.type === 'completed' ? 'success' : 'secondary'})`} strokeWidth={2} />
                        <div>
                          <div className={`text-sm font-medium text-${activity.type === 'completed' ? 'success' : 'secondary'}-800`}>
                            {activity.title}
                          </div>
                          <div className={`text-xs text-${activity.type === 'completed' ? 'success' : 'secondary'}-600`}>
                            {activity.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
                <h3 className="mb-4 text-lg font-semibold font-heading text-text-primary">
                  Learning Goals
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">Complete All Chapters</span>
                      <span className="text-sm text-text-secondary">2/3</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-border-light">
                      <div className="h-2 rounded-full bg-gradient-to-r from-primary to-primary-600 progress-animation" style={{ width: '67%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">Achieve 85%+ Average</span>
                      <span className="text-sm text-text-secondary">81.5%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-border-light">
                      <div className="h-2 rounded-full bg-gradient-to-r from-warning to-warning-600 progress-animation" style={{ width: '81.5%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'chapters':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold font-heading text-text-primary">
                  Chapter Progress
                </h3>
                <p className="mt-1 text-text-secondary">
                  Detailed breakdown of your progress through each chapter
                </p>
              </div>
              <Link to="/chapter-overview-dashboard">
                <Button
                  variant="outline"
                  iconName="LayoutDashboard"
                  iconPosition="left"
                >
                  Chapter Dashboard
                </Button>
              </Link>
            </div>

            <div className="space-y-4">
              {dashboardData.map((chapter) => (
                <ChapterProgressCard key={chapter.id} chapter={chapter} />
              ))}
            </div>
          </div>
        );

      case 'analytics':
        return <PerformanceAnalytics />;

      case 'achievements':
        return <AchievementsBadges />;

      case 'timeline':
        return (
          <div className="space-y-6">
            <LearningTimeline />
            <ExportProgress />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-18 bg-background">
      <Header />
      <main className="pt-4 pb-8">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center mb-4 space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
                <Icon name="TrendingUp" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-heading text-text-primary">
                  Learning Progress Tracker
                </h1>
                <p className="text-text-secondary">
                  Monitor your AI learning journey with comprehensive analytics and insights
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                      transition-colors duration-200 min-touch-target
                      ${activeTab === tab.id
                        ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-focus'
                      }
                    `}
                  >
                    <Icon
                      name={tab.icon}
                      size={18}
                      color="currentColor"
                      strokeWidth={activeTab === tab.id ? 2.5 : 2}
                    />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="duration-300 animate-in slide-in-from-bottom-4">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LearningProgressTracker;